import * as THREE from "three";
import { Signal } from "@engine";
import type { SceneContext } from "@engine";
import type { SegmentFactory } from "../level/SegmentFactory";
import { shapeOf } from "../config/shapes";
import type { Shape } from "../level/types";
import type { LevelTheme, MusicConfig } from "../config/themes";
import { DEFAULT_MUSIC, DEFAULT_SKY, themeById } from "../config/themes";
import type { SkyTunnel } from "../fx/SkyTunnel";

const SHAPES: Shape[] = [
  "octagon",
  "square",
  "triangle",
  "pentagon",
  "hexagon",
];

// ThemeService owns the global "look" of a level: scene background,
// distance fog, ambient + key light tone, baseline bloom multiplier,
// per-shape tunnel material color overrides, sky-tunnel star tint, and
// the musical key handed to the MusicDirector.
//
// Call apply() at the start of every story level (and on entry to
// endless / daily). Tints fall back to the per-shape defaults from
// shapes.ts when a theme doesn't override them, so shape recognition
// stays consistent across themes.
export class ThemeService {
  // Emitted whenever the active theme's musical key/scale changes. The
  // MusicDirector subscribes to re-pitch its layers. This indirection
  // avoids ThemeService having to know about the audio module.
  readonly onMusicConfig = new Signal<MusicConfig>();

  private scene: SceneContext;
  private factory: SegmentFactory;
  private sky: SkyTunnel | null = null;
  private current: LevelTheme;
  private bloomMultiplier = 1;

  constructor(scene: SceneContext, factory: SegmentFactory) {
    this.scene = scene;
    this.factory = factory;
    this.current = themeById("neon");
  }

  // SkyTunnel is constructed in MorphHexGame after the SceneContext but
  // we inject it here so ThemeService can repaint the stars on apply().
  setSkyTunnel(sky: SkyTunnel): void {
    this.sky = sky;
    // Repaint with the currently-active theme so the sky doesn't lag.
    const cfg = this.current.sky ?? DEFAULT_SKY;
    sky.setTheme(cfg.tintA, cfg.tintB, cfg.density);
  }

  apply(theme: LevelTheme): void {
    this.current = theme;
    this.bloomMultiplier = theme.bloomStrength;

    // Scene background + fog. We mutate the existing color/fog instances
    // so that no other engine code holding a reference becomes stale.
    const bg = this.scene.scene.background as THREE.Color | null;
    if (bg && bg.isColor) {
      bg.setHex(theme.background);
    } else {
      this.scene.scene.background = new THREE.Color(theme.background);
    }
    const fog = this.scene.scene.fog as THREE.Fog | null;
    if (fog && fog.isFog) {
      fog.color.setHex(theme.fogColor);
      fog.near = theme.fogNear;
      fog.far = theme.fogFar;
    } else {
      this.scene.scene.fog = new THREE.Fog(
        theme.fogColor,
        theme.fogNear,
        theme.fogFar
      );
    }

    // Lights.
    this.scene.ambient.color.setHex(theme.ambient.color);
    this.scene.ambient.intensity = theme.ambient.intensity;
    this.scene.keyLight.color.setHex(theme.key.color);
    this.scene.keyLight.intensity = theme.key.intensity;

    // Wall materials.
    this.applyShapeTints(theme);

    // Sky tunnel.
    if (this.sky) {
      const cfg = theme.sky ?? DEFAULT_SKY;
      this.sky.setTheme(cfg.tintA, cfg.tintB, cfg.density);
    }

    // Music key. Emitted last so layers re-pitch AFTER visuals refresh.
    this.onMusicConfig.emit(theme.music ?? DEFAULT_MUSIC);
  }

  // Multiplier the game layer composes onto its baseline bloom strength.
  // 1 = neutral, >1 = brighter on this theme.
  getBloomMultiplier(): number {
    return this.bloomMultiplier;
  }

  getCurrent(): LevelTheme {
    return this.current;
  }

  getMusicConfig(): MusicConfig {
    return this.current.music ?? DEFAULT_MUSIC;
  }

  private applyShapeTints(theme: LevelTheme): void {
    for (const shape of SHAPES) {
      const mat = this.factory.getMaterialByShape(shape);
      if (!mat) continue;
      const fallback = shapeOf(shape);
      const tint = theme.shapeTint?.[shape];
      mat.color.setHex(tint?.primary ?? fallback.primary);
      mat.emissive.setHex(tint?.emissive ?? fallback.emissive);
    }
  }
}
